import type { ScanServiceType, SelectedFiles } from '@witmetrics/utils';
import {
  DEFAULT_BLEACHING_TRAY_PARAMETERS,
  DEFAULT_NIGHTGUARD_PARAMETERS,
  DEFAULT_ORTHO_PARAMETERS,
  DEFAULT_RETAINER_PARAMETERS,
  FILE_TYPES,
} from '@witmetrics/utils/constants';
import {
  isBleachingTrayService,
  isNightguardService,
  isOrthoService,
  isRetainerService,
} from '@witmetrics/utils/scanServices';
import { formatNumber, isString } from '@witmetrics/utils/strings';
import {
  BLEACHING_FLOAT_PARAMS,
  type BleachingFloatParams,
  type BleachingFormParams,
  type FloatFieldValue,
  type FormParameters,
  NIGHTGUARD_FLOAT_PARAMS,
  type NightguardFloatParams,
  type NightguardFormParams,
  ORTHO_FLOAT_PARAMS,
  type OrthoFloatParams,
  type OrthoFormParams,
  RETAINER_FLOAT_PARAMS,
  type RetainerFloatParams,
  type RetainerFormParams,
} from '@witmetrics/unison-core';
import {
  DEFAULT_ALIGNER_DATA,
  RELU_LOWER_ARCH,
  RELU_UPPER_ARCH,
} from './constants';

const {
  PHOTO_OPEN_SMILE,
  PHOTO_CLOSED_SMILE,
  PHOTO_FACE_LEFT,
  PHOTO_FACE_RIGHT,
  PHOTO_UPPER_ARCH,
  PHOTO_LOWER_ARCH,
  PHOTO_BUCCAL_LEFT,
  PHOTO_BUCCAL_RIGHT,
  PHOTO_ANTERIOR,
  PANO,
  UPPER_ARCH,
  LOWER_ARCH,
  DOCUMENT,
} = FILE_TYPES;

export function buildInitialParams(
  serviceType?: ScanServiceType
): FormParameters {
  // Form data will depend on the project type; NOTE: Need to convert float params to locale-formatted strings for <NumberInput /> & parse before sending to API
  if (isBleachingTrayService(serviceType)) return buildInitialBleachingParams();
  if (isNightguardService(serviceType)) return buildInitialNightguardParams();
  if (isOrthoService(serviceType)) return buildInitialOrthoParams();
  if (isRetainerService(serviceType)) return buildInitialRetainerParams();
  return DEFAULT_ALIGNER_DATA; // TODO: Defaulting to aligners for now
}

function buildInitialBleachingParams(): BleachingFormParams {
  return {
    ...DEFAULT_BLEACHING_TRAY_PARAMETERS,
    ...(BLEACHING_FLOAT_PARAMS.reduce(
      (a, b) => ({
        ...a,
        [b]: {
          label: formatNumber(DEFAULT_BLEACHING_TRAY_PARAMETERS[b]),
          value: DEFAULT_BLEACHING_TRAY_PARAMETERS[b],
        },
      }),
      {}
    ) as Record<BleachingFloatParams, FloatFieldValue>),
  };
}

function buildInitialNightguardParams(): NightguardFormParams {
  return {
    ...DEFAULT_NIGHTGUARD_PARAMETERS,
    ...(NIGHTGUARD_FLOAT_PARAMS.reduce((a, b) => {
      let val = DEFAULT_NIGHTGUARD_PARAMETERS[b];
      if (isString(val)) val = parseFloat(val);
      return {
        ...a,
        [b]: {
          label: val === null ? '' : formatNumber(val),
          value: DEFAULT_NIGHTGUARD_PARAMETERS[b],
        },
      };
    }, {}) as Record<NightguardFloatParams, FloatFieldValue>),
  };
}

function buildInitialOrthoParams(): OrthoFormParams {
  return {
    ...DEFAULT_ORTHO_PARAMETERS,
    ...(ORTHO_FLOAT_PARAMS.reduce((a, b) => {
      const val = DEFAULT_ORTHO_PARAMETERS[b];
      return {
        ...a,
        [b]: {
          label: val === null ? '' : formatNumber(val),
          value: DEFAULT_ORTHO_PARAMETERS[b],
        },
      };
    }, {}) as Record<OrthoFloatParams, FloatFieldValue>),
  };
}

function buildInitialRetainerParams(): RetainerFormParams {
  return {
    ...DEFAULT_RETAINER_PARAMETERS,
    ...(RETAINER_FLOAT_PARAMS.reduce(
      (a, b) => ({
        ...a,
        [b]: {
          label: formatNumber(DEFAULT_RETAINER_PARAMETERS[b]),
          value: DEFAULT_RETAINER_PARAMETERS[b],
        },
      }),
      {}
    ) as Record<RetainerFloatParams, FloatFieldValue>),
  };
}

export function buildInitialFiles(): SelectedFiles {
  // Start with empty arrays of every possible file type
  return {
    [RELU_UPPER_ARCH]: [],
    [RELU_LOWER_ARCH]: [],
    [UPPER_ARCH.name]: [],
    [LOWER_ARCH.name]: [],
    [PHOTO_OPEN_SMILE.name]: [],
    [PHOTO_CLOSED_SMILE.name]: [],
    [PHOTO_FACE_LEFT.name]: [],
    [PHOTO_FACE_RIGHT.name]: [],
    [PHOTO_UPPER_ARCH.name]: [],
    [PHOTO_LOWER_ARCH.name]: [],
    [PHOTO_BUCCAL_LEFT.name]: [],
    [PHOTO_BUCCAL_RIGHT.name]: [],
    [PHOTO_ANTERIOR.name]: [],
    [PANO.name]: [],
    [DOCUMENT.name]: [],
  };
}
